import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, setCsrfToken } from './api';

const API = process.env.REACT_APP_BACKEND_URL;

const ERRORS = {
  unauthorized: 'Dieses Microsoft-Konto ist für den Admin-Bereich nicht berechtigt. Die Anmeldung wurde beendet.',
  state: 'Die Anmeldung ist abgelaufen oder ungültig. Bitte versuche es erneut.',
  token: 'Die Microsoft-Anmeldung konnte nicht abgeschlossen werden. Bitte versuche es erneut.',
  nonce: 'Sicherheitsprüfung fehlgeschlagen. Bitte versuche es erneut.',
  tenant: 'Dieses Konto gehört nicht zum berechtigten Organisations-Mandanten.',
  microsoft_access_denied: 'Die Anmeldung wurde abgebrochen.',
};

export default function AdminLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const errorKey = params.get('error');
  const oauthError = errorKey ? ERRORS[errorKey] || 'Die Anmeldung ist fehlgeschlagen. Bitte versuche es erneut.' : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Bitte gib E-Mail-Adresse und Passwort ein.');
      return;
    }
    setLoading(true);
    try {
      const data = await api('/auth/password-login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      setCsrfToken(data.csrf_token);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Anmeldung fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-beige px-4 py-10">
      <Helmet>
        <title>Admin-Anmeldung | Felix Genuss</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-md border border-brand-line bg-brand-cream p-8 sm:p-10">
        <img src="/assets/brand/logo-256.webp" alt="Felix Genuss Logo" width="72" height="72" className="mx-auto h-18 w-18 rounded-full" />
        <h1 className="mt-5 text-center font-serif text-3xl font-bold">Admin-Bereich</h1>
        <p className="mt-2 text-center text-sm text-brand-brown-soft">
          Melde dich mit deinem Microsoft-Konto oder mit E-Mail und Passwort an.
        </p>
        {oauthError && (
          <p role="alert" className="mt-5 rounded-sm border border-red-700 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800" data-testid="admin-login-error">
            {oauthError}
          </p>
        )}
        <a
          href={`${API}/api/auth/microsoft/login`}
          className="btn-primary mt-6 w-full"
          data-testid="admin-microsoft-login"
        >
          Mit Microsoft anmelden
        </a>

        <div className="my-6 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-brand-line" />
          <span className="text-xs font-semibold uppercase tracking-wider text-brand-brown-soft">oder</span>
          <span className="h-px flex-1 bg-brand-line" />
        </div>

        <form onSubmit={onSubmit} noValidate data-testid="admin-password-form">
          <div>
            <label htmlFor="admin-email" className="mb-1 block text-sm font-semibold">E-Mail-Adresse</label>
            <input
              id="admin-email" type="email" autoComplete="username" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="min-h-12 w-full rounded-sm border border-brand-brown/25 bg-white px-4"
              data-testid="admin-email-input"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="admin-password" className="mb-1 block text-sm font-semibold">Passwort</label>
            <input
              id="admin-password" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="min-h-12 w-full rounded-sm border border-brand-brown/25 bg-white px-4"
              data-testid="admin-password-input"
            />
          </div>
          {error && (
            <p role="alert" className="mt-4 rounded-sm border border-red-700 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800" data-testid="admin-password-error">
              {error}
            </p>
          )}
          <button type="submit" disabled={loading} className="btn-primary mt-5 w-full disabled:opacity-60" data-testid="admin-password-submit">
            {loading ? 'Anmeldung läuft …' : 'Anmelden'}
          </button>
        </form>
        <p className="mt-5 text-center text-xs text-brand-brown-soft">
          Nur autorisierte Konten erhalten Zugriff. <a href="/" className="link-inline">Zur Website</a>
        </p>
      </div>
    </div>
  );
}
